import { useMemo } from 'react'
import type { SWRConfiguration } from 'swr'
import useSWRImmutable from 'swr/immutable'

import { fetchProcessedTherapists } from '@/legacy-src/utilities/apiUtils'

import { useAppSelector } from '../store/hooks'
import { fetchingSelector } from '../store/selectors/GeneralSelectors'
import { type TherapistType } from '../types/TherapistStateType'
import useUser from './useUser'

interface TherapistsData {
  readonly therapists?: TherapistType[]
  readonly visitedTherapists?: TherapistType[]
  readonly isLoadingTherapists: boolean
}

export const USE_THERAPISTS_SWR_KEY = 'useTherapists'

const useTherapists = (view: 'all' | 'visible', options?: SWRConfiguration): TherapistsData => {
  const { data: allTherapists } = useSWRImmutable(USE_THERAPISTS_SWR_KEY, fetchProcessedTherapists, options)
  const { user } = useUser()

  const subscriptionTherapistIds = useMemo(
    () => user.userSubscriptions?.map((subscription) => subscription.therapistId) || [],
    [user.userSubscriptions],
  )

  const visibleOrVisitedTherapists = useMemo(
    () =>
      allTherapists?.filter(
        (therapist) =>
          therapist.visible ||
          user.visitedTherapistIds.includes(therapist.therapistId) ||
          subscriptionTherapistIds.includes(therapist.therapistId),
      ),
    [allTherapists, user.visitedTherapistIds, subscriptionTherapistIds],
  )

  const visitedTherapists = useMemo(
    () => allTherapists?.filter((therapist) => user?.visitedTherapistIds.includes(therapist.therapistId)),
    [allTherapists, user?.visitedTherapistIds],
  )

  const isLoadingAll = !allTherapists
  const isLoadingVisibleOrVisited = useAppSelector((state) => fetchingSelector(state, 'therapists'))

  return {
    therapists: view === 'all' ? allTherapists : visibleOrVisitedTherapists,
    visitedTherapists,
    isLoadingTherapists: isLoadingAll || isLoadingVisibleOrVisited,
  }
}

export default useTherapists
